import React from 'react'

// Components
import { AsyncButton } from 'components/Button'
import { PlanPicker } from '../../components'
import BillSimulation from 'modules/workspace/components/BillSimulation'
import AddEditors from 'modules/workspace/components/AddEditors'
import Tooltip from 'components/Tooltip'

// GQL
import {
  BillingPlanFragment,
  useDraftBillingSummaryQuery,
  GetWorkspaceSubscribeInformationQuery,
} from 'gqlTypes'

// Styles
import {
  Wrapper,
  Container,
  PlanPickerWrapper,
  Section,
  BillSummaryWrapper,
  TextWithTooltip,
  TooltipContainer,
  TooltipBodyWrapper,
  TooltipText,
  QuestionMarkIcon,
} from './WorkspaceSubscribePlanV2.styles'
import {
  H1,
  H2,
} from 'modules/workspace/views/WorkspaceSubscribeView/WorkspaceSubscribeView.styles'

// Types
export interface WorkspaceSubscribePlanProps {
  plans: BillingPlanFragment[]
  selectedPlan?: BillingPlanFragment
  onPlanChange: (plan: BillingPlanFragment) => void
  onPaymentDetails: () => Promise<void>
  customer: NonNullable<
    GetWorkspaceSubscribeInformationQuery['workspace']['customer']
  >
  discountCode?: string
  onValidDiscountCode: (discountCode: string) => void
  onClearDiscountCode?: () => void
  numberEditors: number
  onEditorsChange?: (editors: number) => void
}

const TOOLTIP_TEXT = (
  <TooltipBodyWrapper>
    <TooltipText>
      <b>How Pricing Works</b>
    </TooltipText>
    <TooltipText>
      <b>Viewers</b> can browse, inspect and comment on documents in the web
      app. Adding Viewers is free!
    </TooltipText>
    <TooltipText>
      <b>Editors</b> can do all of the above, and create and edit documents in
      the Mac app.
    </TooltipText>
    <TooltipText>You can add more Editors to the Workspace later.</TooltipText>
  </TooltipBodyWrapper>
)

export const WorkspaceSubscribePlan: React.FC<WorkspaceSubscribePlanProps> = ({
  customer,
  plans,
  selectedPlan,
  onPlanChange,
  onPaymentDetails,
  discountCode,
  onValidDiscountCode,
  onClearDiscountCode,
  numberEditors,
  onEditorsChange,
}) => {
  // Customer seats
  const numberOfExtraSeats = customer.billing!.seats.availableSeats

  const { data } = useDraftBillingSummaryQuery({
    variables: {
      planId: selectedPlan?.id || '',
      seats: numberEditors,
    },
    skip: !selectedPlan,
  })

  const editorCost = data
    ? `${data.draftBillingSummary.billingCycleAmount}` // Type casting to a string to avoid 0 being falsy
    : undefined
  const isDisabled = !selectedPlan || editorCost === '0'

  const handlePlanChange = (plan: BillingPlanFragment) => {
    onPlanChange(plan)
    onClearDiscountCode?.()
  }

  return (
    <Wrapper>
      <H1>Set Up a Subscription</H1>

      <Container>
        <div>
          <Section>
            <H2>Choose Plan</H2>

            <TextWithTooltip>
              You only pay for users who edit Sketch documents.
              <TooltipContainer>
                <Tooltip content={TOOLTIP_TEXT}>
                  <QuestionMarkIcon />
                </Tooltip>
              </TooltipContainer>
            </TextWithTooltip>

            <PlanPickerWrapper>
              <PlanPicker
                plans={plans}
                onChange={handlePlanChange}
                selectedPlan={selectedPlan}
                isDisabled={editorCost === '0'}
              />
            </PlanPickerWrapper>
          </Section>
          <Section>
            <H2>Add Editors</H2>
            <AddEditors
              seats={numberEditors}
              onChange={editors => onEditorsChange?.(editors)}
            />
          </Section>
        </div>

        <div>
          <BillSummaryWrapper>
            <H2>Bill Summary</H2>
            <BillSimulation
              plan={selectedPlan!}
              trialEnd={customer.billing!.trialEnd!}
              numberEditors={numberEditors}
              numberExtraSeats={numberOfExtraSeats}
              showDiscountInput
              discountCode={discountCode}
              onValidDiscountCode={onValidDiscountCode}
              onClearDiscountCode={onClearDiscountCode}
            />
          </BillSummaryWrapper>

          <AsyncButton
            variant="primary-untinted"
            fill
            onClick={onPaymentDetails}
            disabled={isDisabled}>
            Continue to Payment
          </AsyncButton>
        </div>
      </Container>
    </Wrapper>
  )
}
